import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import Antibody from 'antibodies/Antibody';
import {ANTIBODY_REST_PROPS_BY_ID} from 'antibodies/AntibodyConstants';
import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {distinctDomainToKey, formatToSortedDistinctDomains} from 'antibodies/AntibodyUtils';
import {AntibodyDomainSearchInput} from 'antibodies/create/AntibodyDomainSearchInput';
import {FONT_PROXIMA_12} from 'styles/FontConstants';

export interface AntibodyDomainInputsProps {
  disabled: boolean;
  format: AntibodyFormat;
  proteinsByDistinctDomainKey: Record<string, string | null>;
  setProteinsByDistinctDomainKey: (newProteinsByDomainRef: Record<string, string | null>) => void;
}

/**
 * A set of ChipSearchInput that can be used to associate AA Sequence entities with antibody domains.
 * @param disabled Whether the ChipSearchInput should be disabled
 * @param format The format of the antibody being created
 * @param proteinsByDistinctDomainKey A map of serialized DistinctDomain to AA Sequence API IDs
 * @param setProteinsByDistinctDomainKey A function to update proteinsByDistinctDomainKey
 */
export default function AntibodyDomainInputs({
  disabled,
  format,
  proteinsByDistinctDomainKey,
  setProteinsByDistinctDomainKey,
}: AntibodyDomainInputsProps): JSX.Element {
  const antibodyRestProps = ANTIBODY_REST_PROPS_BY_ID[format.id];
  const sortedDistinctDomains = formatToSortedDistinctDomains(format);

  return (
    <>
      <hr />
      <div className={css(styles.sectionTitle)}>Format</div>
      <div className={css(styles.wrapper)}>
        <div className={css(styles.column)}>
          <div className={css(styles.columnHeader)}>Preview</div>
          <div className={css(styles.vizWrapper)}>
            <Antibody format={format} {...antibodyRestProps} />
          </div>
        </div>
        <div className={css(styles.column)}>
          <div className={css(styles.columnHeader)}>Domains</div>
          <div className={css(styles.column, styles.domainFields)}>
            {sortedDistinctDomains.map((distinctDomain) => {
              const distinctDomainKey = distinctDomainToKey(distinctDomain);
              return (
                <AntibodyDomainSearchInput
                  key={distinctDomainKey}
                  disabled={disabled}
                  distinctDomain={distinctDomain}
                  selectedProtein={proteinsByDistinctDomainKey[distinctDomainKey]}
                  setSelectedProtein={(newSelectedProtein) => {
                    setProteinsByDistinctDomainKey({
                      ...proteinsByDistinctDomainKey,
                      [distinctDomainKey]: newSelectedProtein,
                    });
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  column: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
  },
  columnHeader: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_700,
    fontWeight: 600,
    marginBottom: '8px',
  },
  domainFields: {
    flexWrap: 'wrap',
  },
  sectionTitle: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_600,
    fontWeight: 600,
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
  vizWrapper: {
    backgroundColor: Color.GRAY_50,
    borderRadius: '8px',
  },
  wrapper: {
    display: 'flex',
    gap: '16px',
  },
});
